import React from 'react';
import { makeStyles } from '@material-ui/core/styles';

export type RobotMood = 'idle' | 'thinking' | 'happy';

const useStyles = makeStyles({
  // SVG parts animate around their own centre, not the canvas origin.
  part: {
    transformBox: 'fill-box' as any,
    transformOrigin: 'center',
  },
  eyes: {
    animation: '$blink 4.2s infinite',
  },
  eyesThinking: {
    animation: '$look 1.6s infinite ease-in-out',
  },
  antennaTip: {
    animation: '$glow 2.4s infinite ease-in-out',
  },
  antennaTipThinking: {
    animation: '$glow 0.6s infinite ease-in-out',
  },
  '@keyframes blink': {
    '0%, 90%, 100%': { transform: 'scaleY(1)' },
    '94%': { transform: 'scaleY(0.1)' },
  },
  '@keyframes look': {
    '0%, 100%': { transform: 'translateX(0)' },
    '30%': { transform: 'translateX(-2.2px)' },
    '70%': { transform: 'translateX(2.2px)' },
  },
  '@keyframes glow': {
    '0%, 100%': { opacity: 1 },
    '50%': { opacity: 0.35 },
  },
  '@media (prefers-reduced-motion: reduce)': {
    eyes: { animation: 'none' },
    eyesThinking: { animation: 'none' },
    antennaTip: { animation: 'none' },
    antennaTipThinking: { animation: 'none' },
  },
});

interface IProps {
  size?: number;
  mood?: RobotMood;
}

/**
 * The assistant's face: a small, cheerful robot. Drawn inline so it can blink,
 * look around while it thinks and smile with its eyes when it is pleased.
 * Decorative only — whatever it means is said in text next to it.
 */
const RobotFace: React.FC<IProps> = ({ size = 32, mood = 'idle' }) => {
  const classes = useStyles();
  const thinking = mood === 'thinking';
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none" aria-hidden="true">
      {/* antenna */}
      <path d="M24 9V4.5" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
      <circle
        cx="24"
        cy="4"
        r="3"
        fill="#FFD66B"
        className={`${classes.part} ${thinking ? classes.antennaTipThinking : classes.antennaTip}`}
      />
      {/* ears */}
      <rect x="3.5" y="20" width="4" height="10" rx="2" fill="currentColor" opacity="0.9" />
      <rect x="40.5" y="20" width="4" height="10" rx="2" fill="currentColor" opacity="0.9" />
      {/* head */}
      <rect x="7" y="9" width="34" height="31" rx="11" fill="currentColor" />
      {/* face screen */}
      <rect x="11" y="14" width="26" height="20" rx="8" fill="#0A2A5C" />
      {/* eyes */}
      {mood === 'happy' ? (
        <g stroke="#7FF0FF" strokeWidth="2.6" strokeLinecap="round" fill="none">
          <path d="M15.5 23.5q2.5-3.2 5 0" />
          <path d="M27.5 23.5q2.5-3.2 5 0" />
        </g>
      ) : (
        <g className={`${classes.part} ${thinking ? classes.eyesThinking : classes.eyes}`}>
          <rect x="15.5" y="19.5" width="5" height="6" rx="2.5" fill="#7FF0FF" />
          <rect x="27.5" y="19.5" width="5" height="6" rx="2.5" fill="#7FF0FF" />
        </g>
      )}
      {/* smile */}
      <path
        d={thinking ? 'M21 29.5h6' : 'M19.5 28.5q4.5 3.6 9 0'}
        stroke="#7FF0FF"
        strokeWidth="2.2"
        strokeLinecap="round"
        fill="none"
      />
      {/* cheeks */}
      {!thinking && (
        <g fill="#FF8FB1" opacity="0.75">
          <circle cx="14.5" cy="29" r="1.6" />
          <circle cx="33.5" cy="29" r="1.6" />
        </g>
      )}
    </svg>
  );
};

export default RobotFace;
